import lscache from 'lscache';
import AuthButton from 'modules/authentication/components/AuthButtons/AuthButton';
import { signIn } from 'modules/authentication/helpers/auth';
import React, { useState } from 'react';

export type SocialProvider = 'google' | 'facebook' | 'apple';

type SocialLoginProps = {
  provider: SocialProvider;
  callbackUrl?: string;
  onLoginResult: (
    state: any,
  ) => ({
    error,
    success,
    redirectUrl,
  }: {
    error?: any;
    success: boolean;
    redirectUrl?: string;
  }) => void;
  afterLogin?: () => boolean;
  isLastUsed?: boolean;
  variant?: 'compact' | 'large';
};

const INITIAL_STATE = {
  loading: false,
  error: null,
};

function SocialLogin({
  provider,
  callbackUrl,
  afterLogin,
  onLoginResult,
  isLastUsed,
  variant,
}: SocialLoginProps) {
  const [state, setState] = useState(INITIAL_STATE);

  const onLoginClick = async () => {
    setState({ error: null, loading: true });
    try {
      let wasSuccessful = false;
      const originalCallback = onLoginResult(setState);

      // the wrapping is needed for inapp-browser login flows where the redirect happens after the callback
      const wrappedCallback = (result: {
        error?: any;
        success: boolean;
        redirectUrl?: string;
      }) => {
        wasSuccessful = result.success;
        return originalCallback(result);
      };

      await signIn(provider, callbackUrl, wrappedCallback);
      lscache.set('lastUsedProvider', provider);
      if (wasSuccessful) {
        const hideLoading = afterLogin?.();
        if (hideLoading) {
          setState({ error: null, loading: false });
        }
      }
    } catch (exception) {
      console.error(`${provider} login error:`, exception);
      setState({ error: exception, loading: false });
    }
  };

  return (
    <AuthButton
      onClick={onLoginClick}
      provider={provider}
      isLoading={state.loading}
      hasError={!!state.error}
      isLastUsed={isLastUsed}
      variant={variant}
    />
  );
}

export default SocialLogin;
